import { syntaxTree } from '@codemirror/language';
import { RangeSetBuilder } from '@codemirror/state';
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from '@codemirror/view';

/**
 * Obsidian-style "Live Preview" decorations over a CodeMirror 6
 * markdown buffer. The canonical document is always plain Markdown;
 * this plugin layers visual styling and selectively hides syntax
 * marks when the cursor is not inside the construct.
 *
 * See docs/research/OBSIDIAN.md for the architectural rationale.
 *
 * Decoration rules (DESIGN §8.5):
 *
 *   ATXHeading{1,2,3} — line class + replace leading mark when out
 *   StrongEmphasis    — mark inner; replace marks when out
 *   Emphasis          — mark inner; replace marks when out
 *   InlineCode        — mark inner; replace backticks when out
 *   Link              — mark label; replace [/](url) when out
 *   BulletList        — line class
 *   OrderedList       — line class
 *   Blockquote        — line class + replace leading "> " when out
 *   HorizontalRule    — replace whole line with <hr> widget when out
 *
 * "Out" = `selection.head < node.from || selection.head > node.to`.
 * We use head (not the whole range) so a selection that touches the
 * node still keeps the syntax revealed.
 */

class HRWidget extends WidgetType {
  toDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'cm-live-hr';
    span.setAttribute('aria-hidden', 'true');
    return span;
  }
  ignoreEvent(): boolean {
    return false;
  }
}
const hrWidget = new HRWidget();

const headingLineDecos: Record<1 | 2 | 3, Decoration> = {
  1: Decoration.line({ attributes: { class: 'cm-live-heading-1' } }),
  2: Decoration.line({ attributes: { class: 'cm-live-heading-2' } }),
  3: Decoration.line({ attributes: { class: 'cm-live-heading-3' } }),
};
const bulletLine = Decoration.line({ attributes: { class: 'cm-live-bullet-line' } });
const orderedLine = Decoration.line({ attributes: { class: 'cm-live-ordered-line' } });
const quoteLine = Decoration.line({ attributes: { class: 'cm-live-quote-line' } });

const boldMark = Decoration.mark({ class: 'cm-live-bold' });
const italicMark = Decoration.mark({ class: 'cm-live-italic' });
const codeMark = Decoration.mark({ class: 'cm-live-code' });
const linkMark = Decoration.mark({ class: 'cm-live-link' });

const replaceHide = Decoration.replace({});

interface PendingDecoration {
  from: number;
  to: number;
  deco: Decoration;
  /** Lower side-priority numbers run first. */
  side: number;
}

function build(view: EditorView): DecorationSet {
  const head = view.state.selection.main.head;
  const list: PendingDecoration[] = [];

  for (const { from, to } of view.visibleRanges) {
    syntaxTree(view.state).iterate({
      from,
      to,
      enter(node) {
        const cursorIn = head >= node.from && head <= node.to;

        // ── Headings ──────────────────────────────────────
        if (node.name === 'ATXHeading1' || node.name === 'ATXHeading2' || node.name === 'ATXHeading3') {
          const level = (Number(node.name.slice(-1)) as 1 | 2 | 3);
          const lineFrom = view.state.doc.lineAt(node.from).from;
          list.push({ from: lineFrom, to: lineFrom, deco: headingLineDecos[level], side: -1 });

          if (!cursorIn) {
            // Hide leading "# " (or "## ", "### "). The HeaderMark
            // child holds just the hashes; we extend by one space.
            const child = node.node.firstChild;
            if (child && child.name === 'HeaderMark') {
              const stop = Math.min(child.to + 1, node.to);
              list.push({ from: child.from, to: stop, deco: replaceHide, side: 1 });
            }
          }
          return;
        }

        // ── Strong / emphasis / inline code ───────────────
        if (node.name === 'StrongEmphasis' || node.name === 'Emphasis' || node.name === 'InlineCode') {
          const inner =
            node.name === 'StrongEmphasis' ? boldMark : node.name === 'Emphasis' ? italicMark : codeMark;
          // Find the mark child(ren). For Strong/Emphasis the inner
          // text starts after EmphasisMark and ends before the
          // closing one. For InlineCode the marks are CodeMark.
          const open = node.node.firstChild;
          const close = node.node.lastChild;
          const innerFrom = open ? open.to : node.from;
          const innerTo = close ? close.from : node.to;
          if (innerTo > innerFrom) {
            list.push({ from: innerFrom, to: innerTo, deco: inner, side: 0 });
          }
          if (!cursorIn) {
            if (open && open !== close) list.push({ from: open.from, to: open.to, deco: replaceHide, side: 1 });
            if (close && close !== open) list.push({ from: close.from, to: close.to, deco: replaceHide, side: 1 });
          }
          return;
        }

        // ── Link ───────────────────────────────────────────
        if (node.name === 'Link') {
          // Tree shape: LinkMark[ "[" ] ... LinkMark[ "]" ] LinkMark[ "(" ] URL LinkMark[ ")" ]
          // For v1 we mark the label range as a link and, when out,
          // hide everything except the label.
          const children: Array<{ from: number; to: number; name: string }> = [];
          node.node.cursor().iterate((c) => {
            children.push({ from: c.from, to: c.to, name: c.name });
            return c.name !== 'Link';
          });
          // First LinkMark = '['; second LinkMark = ']'; URL node = (...).
          const openBracket = children.find((c) => c.name === 'LinkMark');
          const reverseMarks = children.filter((c) => c.name === 'LinkMark').reverse();
          const closeParen = reverseMarks[0];
          const closeBracket = children.find(
            (c, i) => c.name === 'LinkMark' && i > 0 && children[i - 1]?.name !== 'LinkMark',
          ) ?? reverseMarks[1];

          if (openBracket && closeBracket && closeBracket.from > openBracket.to) {
            list.push({
              from: openBracket.to,
              to: closeBracket.from,
              deco: linkMark,
              side: 0,
            });
          }
          if (!cursorIn && openBracket && closeParen && closeParen.to > openBracket.from) {
            // Hide '[' and the entire '](URL)' tail.
            list.push({ from: openBracket.from, to: openBracket.to, deco: replaceHide, side: 1 });
            if (closeBracket) {
              list.push({ from: closeBracket.from, to: closeParen.to, deco: replaceHide, side: 1 });
            }
          }
          return;
        }

        // ── Lists ──────────────────────────────────────────
        if (node.name === 'BulletList' || node.name === 'OrderedList') {
          const lineDeco = node.name === 'BulletList' ? bulletLine : orderedLine;
          let pos = node.from;
          while (pos <= node.to) {
            const line = view.state.doc.lineAt(pos);
            list.push({ from: line.from, to: line.from, deco: lineDeco, side: -1 });
            if (line.to >= node.to) break;
            pos = line.to + 1;
          }
          return;
        }

        // ── Blockquote ─────────────────────────────────────
        if (node.name === 'Blockquote') {
          let pos = node.from;
          while (pos <= node.to) {
            const line = view.state.doc.lineAt(pos);
            list.push({ from: line.from, to: line.from, deco: quoteLine, side: -1 });
            const lineCursorIn = head >= line.from && head <= line.to;
            if (!lineCursorIn) {
              const text = view.state.doc.sliceString(line.from, Math.min(line.from + 2, line.to));
              if (text.startsWith('>')) {
                const hideTo = text.startsWith('> ') ? line.from + 2 : line.from + 1;
                list.push({ from: line.from, to: hideTo, deco: replaceHide, side: 1 });
              }
            }
            if (line.to >= node.to) break;
            pos = line.to + 1;
          }
          return;
        }

        // ── HR ─────────────────────────────────────────────
        if (node.name === 'HorizontalRule') {
          if (!cursorIn) {
            const line = view.state.doc.lineAt(node.from);
            list.push({
              from: line.from,
              to: line.to,
              deco: Decoration.replace({ widget: hrWidget }),
              side: 1,
            });
          }
          return;
        }
      },
    });
  }

  // RangeSetBuilder requires from-ascending insertion. Sort then
  // build, dropping any duplicates at the same position.
  list.sort((a, b) => (a.from === b.from ? a.side - b.side : a.from - b.from));
  const builder = new RangeSetBuilder<Decoration>();
  for (const e of list) {
    builder.add(e.from, e.to, e.deco);
  }
  return builder.finish();
}

export const liveMarkdownPreview = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = build(view);
    }
    update(u: ViewUpdate): void {
      if (u.docChanged || u.viewportChanged || u.selectionSet) {
        this.decorations = build(u.view);
      }
    }
  },
  { decorations: (v) => v.decorations },
);
